import React from 'react'

import { dataTransformationService } from 'features/charts-v2/services/data-transformation-service'
import type { RidgelineRow } from 'features/charts-v2/services/data-transformation-service'
import { BaseChartStrategy } from 'features/charts-v2/strategies/Base'
import { RidgelineChartView } from 'features/charts-v2/strategies/RidgelineChartView'
import { type MetricType } from 'features/charts-v2/types'
import {
  ChartProps,
  TransformedChartData,
  type TransformedDataPoint,
} from 'features/charts-v2/types/chart-strategy'
import { ChartTypeV2 } from 'features/charts-v2/types/chart-types'
import { isRidgelineMetric } from 'features/charts-v2/utils/ridgeline-bins'

/**
 * Ridgeline (distribution) chart strategy.
 *
 * Configures and lays out like the horizontal bar — metric on the top x-axis,
 * one labelled row per breakdown value, sentiment/rating zones behind — but
 * each row draws a smooth distribution curve over the metric's score buckets
 * instead of a bar. Heights are each bucket's % of its row on a shared scale,
 * or raw counts when `scaleVolume` is on.
 */
export class RidgelineChartStrategy extends BaseChartStrategy {
  public readonly chartType = ChartTypeV2.RIDGELINE
  public readonly requiredMetrics: MetricType[] = []
  public readonly optionalMetrics: MetricType[] = []

  public transform(props: ChartProps): TransformedChartData {
    const metric = props.selectedMetrics[0]
    if (!props.data || !isRidgelineMetric(metric)) return { chartData: [] }

    return {
      chartData: dataTransformationService.transformForRidgeline(
        props.data,
        metric,
        props.sortDirection,
      ) as unknown as TransformedDataPoint[],
    }
  }

  public render(
    props: ChartProps & { transformedData: TransformedChartData },
  ): React.ReactElement {
    const validation = this.validate(props)
    if (!validation.isValid) {
      return this.renderValidationError(validation)
    }

    const {
      transformedData,
      onDataPointHover,
      selectedMetrics,
      onSegmentClick,
      fontSizePx = 13,
      showXAxisLabel = true,
      showYAxisLabel = true,
      showDatapointLabels = true,
      yAxisOverflow = 'expand',
      showSentimentZones = true,
      showRatingZones = true,
      scaleVolume = false,
    } = props

    const metric = selectedMetrics[0]
    if (!isRidgelineMetric(metric)) {
      return this.renderValidationError({
        isValid: false,
        errorTitle: 'Unsupported metric',
        errorDescription:
          'Ridgeline charts require a sentiment or rating metric.',
      })
    }

    if (!transformedData.chartData || transformedData.chartData.length === 0) {
      return this.renderEmptyState(
        'No data available',
        'Try adding some filters.',
      )
    }

    return (
      <RidgelineChartView
        rows={transformedData.chartData as unknown as RidgelineRow[]}
        metric={metric}
        display={{
          scaleByVolume: scaleVolume,
          fontSizePx,
          showXAxisLabel,
          showYAxisLabel,
          showDatapointLabels,
          yAxisOverflow,
          showSentimentZones,
          showRatingZones,
          metricLabel: this.getMetricLabel([metric]),
          commonMargin: this.getCommonMargin(),
        }}
        onDataPointHover={onDataPointHover}
        onSegmentClick={onSegmentClick}
      />
    )
  }

  public getTitle(): string {
    return 'Score Distribution'
  }
}
